import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchInstructionChapters,
  fetchInstructionChapter,
  updateInstructionChapter,
  createInstructionChapter,
  deleteInstructionChapter,
} from "../../api/quality/instruction_chapter";
import { normalizeError, hasMeaningfulErrors } from "../../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
  instructionsWithoutChapter: [],
  totalCount: 0,
  currentCount: 0,
  newCount: 0,
};

export const initialController = {
  filters: [],
  page: 0,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

function isHttpFailureStatus(status) {
  const n = Number(status);
  return Number.isFinite(n) && n >= 400;
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const deleteInstructionChapterAsync = createAsyncThunk(
  "instruction_chapters/deleteInstructionChapterAsync",
  async (payload, { rejectWithValue }) => {
    try {
      return await deleteInstructionChapter(payload);
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "delete_instruction_chapter");
    }
  }
);

export const createInstructionChapterAsync = createAsyncThunk(
  "instruction_chapters/createInstructionChapterAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createInstructionChapter(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "create_instruction_chapter");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "create_instruction_chapter");
    }
  }
);

export const updateInstructionChapterAsync = createAsyncThunk(
  "instruction_chapters/updateInstructionChapterAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateInstructionChapter(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "update_instruction_chapter");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_instruction_chapter");
    }
  }
);

export const getInstructionChaptersAsync = createAsyncThunk(
  "instruction_chapters/getInstructionChaptersAsync",
  async (controller = initialController, { rejectWithValue }) => {
    try {
      const response = await fetchInstructionChapters(controller);
      const status = response?.response?.status ?? response?.status;
      if (
        isHttpFailureStatus(status) ||
        response?.error ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(rejectWithValue, response, "list_instruction_chapters");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_instruction_chapters");
    }
  }
);

export const getInstructionChapterAsync = createAsyncThunk(
  "instruction_chapters/getInstructionChapterAsync",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchInstructionChapter(id);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "get_instruction_chapter");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "get_instruction_chapter");
    }
  }
);

const instructionChapterSlice = createSlice({
  name: "instruction_chapters",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getInstructionChaptersAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getInstructionChaptersAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data || [];
        state.instructionsWithoutChapter = action.payload.instructionsWithoutChapter || [];
        state.totalCount = action.payload.totalCount || 0;
        state.currentCount = action.payload.currentCount || 0;
        state.newestInstructionChaptersCount = action.payload.newCount || 0;
      })
      .addCase(getInstructionChaptersAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente instruksjonskapitlene.");
      })
      .addCase(updateInstructionChapterAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateInstructionChapterAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const index = draftState.data.findIndex(
            (type) => type.id === action.payload.id
          );
          draftState.data[index] = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(updateInstructionChapterAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke oppdatere instruksjonskapittelet.");
      })
      .addCase(createInstructionChapterAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createInstructionChapterAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data.push(action.payload);
          draftState.isLoading = false;
        });
      })
      .addCase(createInstructionChapterAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke opprette instruksjonskapittelet.");
      })
      .addCase(deleteInstructionChapterAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(deleteInstructionChapterAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(deleteInstructionChapterAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke slette instruksjonskapittelet.");
      })
      .addCase(getInstructionChapterAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getInstructionChapterAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.chapter = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(getInstructionChapterAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente instruksjonskapittelet.");
      });
  },
});

export const { reducer } = instructionChapterSlice;

export default instructionChapterSlice;
